/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import styled from "@emotion/styled";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useSelect } from "downshift";
import { useRef, ButtonHTMLAttributes } from "react";
import { noop } from "../../../../constants/noop";
import { RequiredThemeKeys } from "../../../../util/theme";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useFocused } from "../../hooks/useFocused";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { BaseOptionType } from "../../types";
import { useSelectIds, stopPropagationHandler, isSelectV2OptionSeparator, findNextValidOptionIndex } from "../../utils";
import { SelectButton } from "../SelectButton";
import { SelectButtonText } from "../SelectButtonText";
import { selectBasicFontTheme, selectV2Theme } from "../selectV2Theme";
import { ActionButtonBaseCss } from "../shared/ActionButton";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButtonIcon } from "../shared/ToggleButtonIcon";
import { ContentBeforeContainer } from "./ContentBeforeContainer";
import { OptionalPropsNowRequired } from "./types";

interface FakeActionButtonProps {
    themeMode: RequiredThemeKeys;
    isOpen?: boolean;
}

// Necessary due to the computeRightPadding in SelectButton.tsx
const ActionsStyled = styled(Actions)`
    position: absolute;
    right: 0;
`;
// The whole select is the toggle button here, so we add a toggle-button-looking thing for
// consistency. Users love consistency and they LOVE chevrons that point down.
// If action button is used, an axe error is thrown because toggle would have a focusable
// element inside it
const FakeActionButton = styled("span")<FakeActionButtonProps>`
    ${({ themeMode }) => ActionButtonBaseCss({ themeMode })}
    ${({ isOpen, themeMode }) =>
        isOpen
            ? `
                background-color:  ${selectV2Theme.actionButtons.background.active(themeMode)};
            `
            : ``}
    &:hover {
        background-color: ${({ themeMode }) => selectV2Theme.actionButtons.background.hover(themeMode)};
    }
    &:active {
        background-color: ${({ themeMode }) => selectV2Theme.actionButtons.background.active(themeMode)};
    }
`;

interface SelectPropsBase<Option extends BaseOptionType>
    extends Omit<OptionalPropsNowRequired<Option, HTMLButtonElement>, "isSearchable" | "onCreateOption"> {
    onChange: (value: Option | null) => void;
    contentBefore?: React.ReactNode;
}

export const SelectBasic = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    clientId,
    contentBefore,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    id,
    innerRef,
    isLoading,
    isOptionDisabled,
    isReadonly,
    isClearable = false,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuPlacement = "bottom",
    menuPosition,
    menuId,
    onBlur: providedOnBlur = noop,
    onChange,
    onFocus: providedOnFocus = noop,
    optionHeight,
    options,
    optionToString,
    portalProps,
    selectedOption,
    getOptionKey,
    validationState,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    onKeyDown: providedOnKeyDown,
}: SelectPropsBase<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const themeMode = useCombinedThemeMode();

    const isInteractive = !isReadonly;
    //TODO: implement this with the new ROOT
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
    const { isFocused, onBlur, onFocus } = useFocused({
        onFocus: providedOnFocus,
        onBlur: providedOnBlur,
    });

    // The flyout needs to point to the component container, but otherwise flyout interactions
    // should affect the input.
    const { ref: resizeRef, width: targetWidth } = useHorizontalResize();
    const localToggleRef = useRef<HTMLButtonElement>(null);
    const toggleButtonRef = useForkRef(localToggleRef, resizeRef, innerRef || null);

    const { closeMenu, getItemProps, getMenuProps, getToggleButtonProps, highlightedIndex, isOpen, openMenu } =
        useSelect({
            ...a11yMessages,
            ...useSelectIds("select", id),
            ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
            selectedItem: null,
            items: options,
            circularNavigation: false,
            itemToString: optionToString,
            stateReducer(state, { changes, type }) {
                switch (type) {
                    case useSelect.stateChangeTypes.MenuKeyDownEscape:
                        // Cancel the escape key handling. We want to handle this key on our own
                        // through the StackOrderManager.
                        return state;
                    case useSelect.stateChangeTypes.MenuKeyDownEnter:
                    case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                    case useSelect.stateChangeTypes.ItemClick:
                        if (changes.selectedItem) {
                            return isOptionDisabled?.(changes.selectedItem) ? state : changes;
                        }
                }

                if (changes.highlightedIndex) {
                    switch (type) {
                        case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                        case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
                            if (isSelectV2OptionSeparator(options[changes.highlightedIndex])) {
                                return {
                                    ...changes,
                                    highlightedIndex: findNextValidOptionIndex(
                                        options,
                                        changes.highlightedIndex,
                                        type === useSelect.stateChangeTypes.MenuKeyDownArrowDown
                                    ),
                                };
                            }
                    }
                }

                return changes;
            },
            onStateChange: ({ type, selectedItem }) => {
                switch (type) {
                    case useSelect.stateChangeTypes.MenuKeyDownEscape:
                    case useSelect.stateChangeTypes.MenuKeyDownEnter:
                    case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                    case useSelect.stateChangeTypes.ItemClick:
                        if (selectedItem) {
                            // TODO are there cases here where the selectedItem is null?
                            onChange(selectedItem);
                        }
                        break;
                }
            },
            onIsOpenChange: ({ isOpen: openState }) => {
                if (process.env.NODE_ENV !== "production") {
                    warnOnceForDeprecatedSetMenuIsOpenProp =
                        warnOnceForDeprecatedSetMenuIsOpenProp ??
                        warnOnce(
                            "SelectV2 (basic): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                        );

                    if (setMenuIsOpen) {
                        warnOnceForDeprecatedSetMenuIsOpenProp();
                    }
                }
                // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
                (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
            },
        });

    const { flyoutProps, targetRef } = useSelectFlyout<HTMLButtonElement>({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: toggleButtonRef,
        menuPlacement,
        menuPosition,
        portalProps,
        updateKey: String(isOpen),
    });

    const toggleButtonProps = getToggleButtonProps({
        // This will remove some mouse and key handling.
        // Unlike useCombobox, we don't need to handle chip navigation which these handlers
        // assist with.
        disabled: !isInteractive,
        ref: targetRef,
        onClick: stopPropagationHandler,
        onBlur,
        onFocus,
        onKeyDown: (e) => {
            if (providedOnKeyDown) {
                providedOnKeyDown(e, {
                    highlightedIndex,
                    options,
                });
            }
        },
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        "aria-describedby": ariaDescribedby,
        id,
    }) as ButtonHTMLAttributes<HTMLButtonElement>;

    // We don't ever want to include this; the button must still be focusable.
    delete toggleButtonProps["disabled"];
    return (
        <Root
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            themeMode={themeMode}
            appearance={appearance}
        >
            <HiddenInput
                clientId={clientId}
                selectionType={selectedOption}
                getHiddenInputValue={getHiddenInputValue}
                hiddenInputName={hiddenInputName}
            />
            <SelectButton
                type="button"
                data-menu-test-id={menuId}
                clientId={getClientId(clientId, "-toggle")}
                hideDropdownArrow={hideDropdownArrow}
                isClearable={isClearable}
                hasContentBefore={!!contentBefore}
                themeMode={themeMode}
                // We'll reattach our own rather than relying on Clickable's ring.
                omitFocusRing
                {...toggleButtonProps}
            >
                {contentBefore ? <ContentBeforeContainer>{contentBefore}</ContentBeforeContainer> : null}
                {formatSelectedValue !== undefined ? (
                    formatSelectedValue({ option: selectedOption })
                ) : (
                    <SelectButtonText {...selectBasicFontTheme.variant[themeMode]} themeMode={themeMode} lineClamp={1}>
                        {optionToString(selectedOption as Option) || ""}
                    </SelectButtonText>
                )}

                {selectedOption === null && placeholder && (
                    <SelectPlaceholder hasContentBefore={!!contentBefore}>{placeholder}</SelectPlaceholder>
                )}
            </SelectButton>
            {isInteractive && (
                <ActionsStyled
                    // This will not be rendered if the control is not interactive.
                    isReadonly={false}
                    themeMode={themeMode}
                >
                    {!hideDropdownArrow && (
                        <FakeActionButton
                            isOpen={isOpen}
                            themeMode={themeMode}
                            onMouseDown={(e) => {
                                if (e.detail >= 2) {
                                    // If this is a doubleclick's mousedown, prevent text
                                    // highlighting. This is only an issue because we're pretending
                                    // this span is a button. Otherwise, the button would block
                                    // text selection on double click.
                                    e.preventDefault();
                                }
                            }}
                            onClick={() => {
                                if (!isOpen) {
                                    openMenu();
                                } else {
                                    closeMenu();
                                }
                            }}
                            onMouseUp={(event) => {
                                if (isOpen) {
                                    // If we don't stop propagation here, downshift will see this
                                    // mouseup as a click outside the component since it's floating
                                    // outside the toggle button. Downshift will then attempt to close
                                    // the menu. Since we want to call openMenu/closeMenu in onClick
                                    // ourselves, we need to tell downshift to chill.
                                    event.stopPropagation();
                                }
                            }}
                        >
                            <ToggleButtonIcon />
                        </FakeActionButton>
                    )}
                    {isClearable && selectedOption && (
                        <SelectClearButton
                            clearSelection={onChange}
                            isMultiSelect={false}
                            resetFiltering={() => {}}
                            refocusTargetElement={() => {
                                localToggleRef.current?.focus();
                            }}
                        />
                    )}
                </ActionsStyled>
            )}
            <SelectMenu<Option>
                allSelected={false}
                enableSelectAll={false}
                extraMenuProps={{
                    onKeyDown: (e) => {
                        providedOnKeyDown?.(e, {
                            highlightedIndex,
                            options,
                        });
                        switch (e.key) {
                            case "Tab":
                                e.preventDefault();
                                localToggleRef.current?.focus();
                                break;
                        }
                    },
                }}
                flyoutProps={flyoutProps}
                formatOption={formatOption}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                inputValue={""}
                isLoading={isLoading}
                isOpen={isOpen}
                isOptionDisabled={isOptionDisabled}
                menuWidth={menuWidth}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                options={options}
                optionToString={optionToString}
                selectedItems={[]}
                selectedOption={selectedOption}
                targetWidth={targetWidth}
                getOptionKey={getOptionKey}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
